import type { RefObject } from "react";
import { useCallback, useRef, useState } from "react";

import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import type { FlashListRef } from "@/ui/layout/list/FlatList";
import { HorizontalListScrollConfig } from "@/ui/layout/list/HorizontalListNavigationButtons";
import { isWeb } from "@/utils/platform.util";

interface UseHorizontalListScrollProps<T> {
  isHorizontal?: boolean;
  isListEmpty: boolean;
  listRef?: RefObject<FlashListRef<T> | null>;
}

interface UseHorizontalListScroll<T> {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  effectiveListRef: RefObject<FlashListRef<T> | null>;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  recheckScrollState: () => void;
}

const useHorizontalListScroll = <T>({
  isHorizontal,
  isListEmpty,
  listRef,
}: UseHorizontalListScrollProps<T>): UseHorizontalListScroll<T> => {
  const internalListRef = useRef<FlashListRef<T> | null>(null);
  const effectiveListRef = listRef || internalListRef;

  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;

      const hasOverflow = contentSize.width > layoutMeasurement.width;
      if (!hasOverflow) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const threshold = HorizontalListScrollConfig.ScrollRoundingThresholdPx;
      const isAtStart = contentOffset.x <= threshold;
      const isAtEnd =
        contentOffset.x + layoutMeasurement.width >=
        contentSize.width - threshold;

      setCanScrollLeft(!isAtStart);
      setCanScrollRight(!isAtEnd);
    },
    [],
  );

  const recheckScrollState = useCallback(() => {
    if (!isWeb() || isListEmpty) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    if (!effectiveListRef.current) {
      return;
    }

    const scrollNode = effectiveListRef.current.getScrollableNode();
    const { clientWidth, scrollLeft, scrollWidth } = scrollNode;

    const hasOverflow = scrollWidth > clientWidth;
    if (!hasOverflow) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const isAtStart = (scrollLeft || 0) <= 1;
    const isAtEnd = (scrollLeft || 0) + clientWidth >= scrollWidth - 1;

    setCanScrollLeft(!isAtStart);
    setCanScrollRight(!isAtEnd);
  }, [effectiveListRef, isListEmpty]);

  return {
    canScrollLeft,
    canScrollRight,
    effectiveListRef,
    onScroll: isHorizontal ? handleScroll : undefined,
    recheckScrollState,
  };
};

export default useHorizontalListScroll;
